/**
 * LabelPicker — Dropdown per assegnare/rimuovere labels da una chat.
 * Etichette collegate a WhatsApp Business (icona telefono): assegnarle o toglierle aggiorna
 * anche il telefono (store -> services/whatsapp/wahaLabels.ts). Solo sulle chat WhatsApp di
 * un numero WAHA compare "Importa etichette da WhatsApp".
 */

import { useState, useRef, useEffect } from 'react';
import { Tag, Plus, Loader2, Smartphone, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { LabelBadge } from './LabelBadge';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { importWahaLabels, type LabelSyncResult } from '@/lib/services/whatsapp/wahaLabels';
import { isLinkedLabel, isWhatsAppChatJid } from '@/lib/services/whatsapp/wahaLabelsLogic';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { useApp } from '@/components/AppProvider';

const PRESET_COLORS = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280'];

interface Props {
  chatJid: string;
  organizationId: string;
}

export function LabelPicker({ chatJid, organizationId }: Props) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3B82F6');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { confirm } = useApp();

  const { orgLabels, chatLabels, fetchOrgLabels, fetchChatLabels, assignLabel, removeLabel, createLabel } = useWhatsAppChatMetaStore();
  const currentLabels = chatLabels.get(chatJid) || [];
  const wahaConfig = useWhatsAppChatStore((s) => s.wahaConfig);
  // Importazione solo per le chat WhatsApp di un numero WAHA di questa organizzazione.
  const importConfigId = wahaConfig
    && wahaConfig.organizationId === organizationId
    && (wahaConfig.serviceName ?? 'waha') === 'waha'
    && isWhatsAppChatJid(chatJid)
    ? wahaConfig.configId
    : null;
  // Numero della chat aperta: le etichette collegate a un altro numero restano solo nel CRM.
  const chatConfigId = wahaConfig && wahaConfig.organizationId === organizationId ? wahaConfig.configId : null;

  useEffect(() => {
    if (open && orgLabels.length === 0) fetchOrgLabels(organizationId);
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setCreating(false); }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async (labelId: string) => {
    const isAssigned = currentLabels.some((l) => l.id === labelId);
    const result: LabelSyncResult = isAssigned
      ? await removeLabel(chatJid, labelId, organizationId, chatConfigId)
      : await assignLabel(chatJid, labelId, organizationId, chatConfigId);
    if (result.status === 'error') {
      const label = orgLabels.find((l) => l.id === labelId);
      toast.error(isLinkedLabel(label)
        ? `Salvata nel CRM ma non sul telefono: ${result.message}`
        : `Etichetta non salvata: ${result.message}`);
    }
  };

  const handleImport = async () => {
    if (!importConfigId) return;
    const ok = await confirm({
      title: 'Importa etichette da WhatsApp',
      message: 'Leggo le etichette di WhatsApp Business di questo numero e le chat a cui sono assegnate, e le aggiungo nel CRM. Sul telefono non cambia nulla e le etichette create nel CRM restano come sono.',
      confirmText: 'Importa',
      cancelText: 'Annulla',
    });
    if (!ok) return;
    setImporting(true);
    try {
      const res = await importWahaLabels(organizationId, importConfigId);
      await fetchOrgLabels(organizationId);
      await fetchChatLabels([chatJid], organizationId);
      toast.success(res.labels === 0
        ? 'Nessuna etichetta trovata su WhatsApp'
        : `Importate ${res.labels} etichette (${res.created} nuove), ${res.assignments} assegnazioni alle chat`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Importazione non riuscita');
    } finally {
      setImporting(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    await createLabel(organizationId, newName.trim(), newColor);
    setNewName('');
    setCreating(false);
    setSaving(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-[rgba(55,53,47,0.12)] dark:border-white/10 bg-white dark:bg-gray-800 text-[#787774] hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors"
        {...tip('Gestisci le etichette della chat')}
      >
        <Tag className="w-3.5 h-3.5" />
        {currentLabels.length === 1 ? '1 etichetta' : currentLabels.length > 1 ? `${currentLabels.length} etichette` : 'Etichette'}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 py-2 min-w-[200px]">
          <div className="px-3 pb-2 border-b border-[rgba(55,53,47,0.06)] dark:border-white/8">
            <p className="text-[10px] font-semibold text-[#787774] uppercase tracking-wide">Labels</p>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {orgLabels.length === 0 && (
              <p className="text-xs text-[#787774] text-center py-3">Nessuna etichetta creata</p>
            )}
            {orgLabels.map((label) => {
              const assigned = currentLabels.some((l) => l.id === label.id);
              return (
                <button
                  key={label.id}
                  onClick={() => handleToggle(label.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors"
                >
                  <span className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 transition-colors ${assigned ? 'border-transparent' : 'border-[rgba(55,53,47,0.2)]'}`}
                    style={assigned ? { backgroundColor: label.color } : {}} />
                  <LabelBadge name={label.name} color={label.color} />
                  {isLinkedLabel(label) && (
                    <span className="ml-auto text-[#9B9A97]" {...iconTip('Collegata a WhatsApp: si aggiorna anche sul telefono')}>
                      <Smartphone className="w-3 h-3" aria-hidden />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-3 pt-2 border-t border-[rgba(55,53,47,0.06)] dark:border-white/8">
            {creating ? (
              <div className="space-y-2">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nome label..."
                  className="w-full text-xs bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded px-2 py-1.5 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button key={c} onClick={() => setNewColor(c)}
                      className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-300' : ''}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCreate} disabled={!newName.trim() || saving}
                    className="flex-1 text-xs bg-[#2383E2] text-white rounded py-1 hover:bg-[#1a6fc2] disabled:opacity-50 flex items-center justify-center gap-1">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Crea'}
                  </button>
                  <button onClick={() => setCreating(false)} className="flex-1 text-xs border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded py-1 text-[#787774] hover:bg-[rgba(55,53,47,0.04)]">
                    Annulla
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setCreating(true)}
                className="w-full flex items-center gap-1.5 text-xs text-[#2383E2] hover:text-[#1a6fc2] transition-colors py-1">
                <Plus className="w-3.5 h-3.5" />
                Nuova etichetta
              </button>
            )}
            {!creating && importConfigId && (
              <button onClick={handleImport} disabled={importing}
                className="w-full flex items-center gap-1.5 text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors py-1 disabled:opacity-50"
                {...tip('Copia nel CRM le etichette di WhatsApp Business di questo numero')}>
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Importa etichette da WhatsApp
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
